import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { youtubeApi, queryKeys } from '@/lib/api';

export default function AdminYouTubeLogsTab() {
  const queryClient = useQueryClient();
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFacility, setFilterFacility] = useState('');

  const queryParams = {
    ...(filterStatus ? { status: filterStatus } : {}),
    ...(filterFacility ? { facilityId: filterFacility } : {}),
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.youtube.logs(queryParams),
    queryFn: () => youtubeApi.getRenameLogs(queryParams),
  });

  const retryMutation = useMutation({
    mutationFn: (id: number) => youtubeApi.retryRename(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.youtube.all });
    },
  });

  const logs = data?.logs || [];

  const statusClass = (status: string) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
      case 'failed':  return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      case 'pending': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
      default:        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Status</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Facility ID</label>
          <input
            type="text"
            value={filterFacility}
            onChange={e => setFilterFacility(e.target.value)}
            placeholder="e.g. fac1"
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="mr-1 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          Failed to load rename logs
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Facility</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Event</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Teams</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Original Title</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">New Title</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Created</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No rename log entries
                  </td>
                </tr>
              ) : (
                logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-xs text-gray-500">{log.facilityId}</td>
                    <td className="px-4 py-3 font-medium">{log.schedule?.eventName || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                      {log.schedule?.team1Name && log.schedule?.team2Name
                        ? `${log.schedule.team1Name} vs ${log.schedule.team2Name}`
                        : '—'}
                    </td>
                    <td className="max-w-xs px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                      <span className="block truncate">{log.originalTitle || '—'}</span>
                    </td>
                    <td className="max-w-xs px-4 py-3 text-xs">
                      <span className="block truncate">{log.newTitle || '—'}</span>
                      {log.errorMessage && (
                        <span className="block truncate text-red-500">{log.errorMessage}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(log.status)}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {log.status === 'failed' && (
                        <button
                          onClick={() => retryMutation.mutate(log.id)}
                          disabled={retryMutation.isPending}
                          title="Retry"
                          className="rounded p-1 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
